import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useState } from "react";

import type { MessageAttachment } from "../../lib/api/messages";
import { downloadMessageAttachment } from "../../lib/api/messages";
import {
    formatMessageFileSize,
    isMessageImageAttachment,
} from "./messageAttachment.utils";

function messageAttachmentUrl(attachmentId: number) {
    const base = import.meta.env.DEV
        ? "/api"
        : (import.meta.env.VITE_API_URL ?? "/api");

    return `${base}/messages/attachments/${attachmentId}`;
}

export default function MessageAttachmentList({
    attachments,
    inverted = false,
}: {
    attachments: MessageAttachment[];
    inverted?: boolean;
}) {
    if (attachments.length === 0) {
        return null;
    }

    return (
        <Stack spacing={0.75} sx={{ mt: 1 }}>
            {attachments.map((attachment) => (
                <MessageAttachmentItem
                    key={attachment.id}
                    attachment={attachment}
                    inverted={inverted}
                />
            ))}
        </Stack>
    );
}

function MessageAttachmentItem({
    attachment,
    inverted,
}: {
    attachment: MessageAttachment;
    inverted?: boolean;
}) {
    const [downloading, setDownloading] = useState(false);
    const isImage = isMessageImageAttachment(attachment.mimeType);
    const borderColor = inverted ? "rgba(255,255,255,0.35)" : "divider";
    const textColor = inverted ? "primary.contrastText" : "text.primary";
    const mutedColor = inverted ? "rgba(255,255,255,0.8)" : "text.secondary";

    async function handleDownload() {
        if (downloading) {
            return;
        }

        setDownloading(true);

        try {
            await downloadMessageAttachment(attachment.id, attachment.originalFilename);
        } finally {
            setDownloading(false);
        }
    }

    return (
        <Box
            sx={{
                border: 1,
                borderColor,
                borderRadius: 1.5,
                overflow: "hidden",
                bgcolor: inverted ? "rgba(255,255,255,0.08)" : "background.paper",
            }}
        >
            {isImage ? (
                <Box
                    component="img"
                    src={messageAttachmentUrl(attachment.id)}
                    alt={attachment.originalFilename}
                    loading="lazy"
                    sx={{
                        display: "block",
                        width: "100%",
                        maxHeight: 220,
                        objectFit: "cover",
                    }}
                />
            ) : null}

            <Stack
                direction="row"
                spacing={1}
                sx={{
                    alignItems: "center",
                    px: 1,
                    py: 0.75,
                    minWidth: 0,
                }}
            >
                {!isImage ? (
                    <InsertDriveFileOutlinedIcon fontSize="small" sx={{ color: mutedColor, flexShrink: 0 }} />
                ) : null}

                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            color: textColor,
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {attachment.originalFilename}
                    </Typography>
                    <Typography variant="caption" sx={{ color: mutedColor }}>
                        {formatMessageFileSize(attachment.fileSize)}
                    </Typography>
                </Box>

                <Button
                    size="small"
                    variant={inverted ? "outlined" : "text"}
                    color={inverted ? "inherit" : "primary"}
                    startIcon={<DownloadIcon />}
                    disabled={downloading}
                    onClick={handleDownload}
                    sx={{
                        flexShrink: 0,
                        ...(inverted
                            ? {
                                  borderColor: "rgba(255,255,255,0.45)",
                                  color: "primary.contrastText",
                              }
                            : {}),
                    }}
                >
                    {downloading ? "..." : "Свали"}
                </Button>
            </Stack>
        </Box>
    );
}
